"use client";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useAction, useQuery } from "convex/react";
import { useEffect, useEffectEvent, useState } from "react";
import { Badge } from "@/components/ui/badge";

type CreatorStreamEmbedProps = {
  creatorId: Id<"users">;
};

type StreamStatus = {
  twitch: boolean;
  youtube: boolean;
};

export function CreatorStreamEmbed({ creatorId }: CreatorStreamEmbedProps) {
  const [streamStatus, setStreamStatus] = useState<StreamStatus>({
    twitch: false,
    youtube: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch creator's streaming platform info
  const creatorInfo = useQuery(api.users.queries.getCreatorStreamInfo, {
    userId: creatorId,
  });

  const getLiveStatuses = useAction(api.twitch.getLiveStatuses);

  const checkStreamStatus = useEffectEvent(async () => {
    const status = await getLiveStatuses({ userId: creatorId });
    setStreamStatus(status);
    setIsLoading(false);
  });

  useEffect(() => {
    if (!creatorInfo) {
      setIsLoading(false);
      return;
    }

    const hasStreamingPlatforms = Boolean(
      creatorInfo.twitchUsername || creatorInfo.youtubeChannelId
    );

    if (!hasStreamingPlatforms) {
      setIsLoading(false);
      return;
    }

    // Check immediately
    checkStreamStatus();

    // Poll every 60 seconds
    const POLL_INTERVAL = 60_000;
    const interval = setInterval(checkStreamStatus, POLL_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [creatorInfo]);

  const isLive = streamStatus.twitch || streamStatus.youtube;

  // Don't render anything while loading or if not live
  if (isLoading || !isLive || !creatorInfo) {
    return null;
  }

  const domain =
    typeof window !== "undefined"
      ? window.location.hostname
      : "bf6loadouts.com";

  return (
    <div className="mb-6 space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-lg">Creator is Live Now</h3>
        <Badge className="animate-pulse bg-red-600 hover:bg-red-600">
          <div className="mr-1.5 h-2 w-2 rounded-full bg-white" />
          LIVE
        </Badge>
      </div>

      {streamStatus.twitch && creatorInfo.twitchUsername && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <svg
              aria-label="Twitch"
              className="h-4 w-4"
              fill="currentColor"
              role="img"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Twitch</title>
              <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
            </svg>
            <span className="font-medium">Twitch</span>
          </div>
          <div className="aspect-video overflow-hidden rounded-md">
            <iframe
              allowFullScreen
              className="h-full w-full"
              src={`https://player.twitch.tv/?channel=${creatorInfo.twitchUsername}&parent=${domain}`}
              title={`${creatorInfo.twitchUsername}'s Twitch stream`}
            />
          </div>
        </div>
      )}

      {streamStatus.youtube && creatorInfo.youtubeChannelId && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <svg
              aria-label="YouTube"
              className="h-4 w-4"
              fill="currentColor"
              role="img"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>YouTube</title>
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            <span className="font-medium">YouTube</span>
          </div>
          <div className="aspect-video overflow-hidden rounded-md">
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
              src={`https://www.youtube.com/embed/live_stream?channel=${creatorInfo.youtubeChannelId}`}
              title="Creator's YouTube stream"
            />
          </div>
        </div>
      )}
    </div>
  );
}
